import { graphql } from 'gatsby';
import * as React from 'react';
import { trackEvent } from '../analytics';
import { useMonthRange } from '../hooks/use-month-range';
import { getNumberParts, isDefined, isFilledArray, parseNumber } from '../lib';
import { Checkbox, CheckboxGroup } from './checkbox';
import { DateField } from './date-field';
import { useLocalize } from './localize-context';
import { MessageBtn } from './message-btn';
import { SelectField } from './select-field';
import { TextField } from './text-field';
import { VisitorContext } from './visitor-context';
import { VisitorTypeSelect } from './visitor-type-select';

type ComboBookingFormProps = {
  combo: ComboDetails;
};

export const ComboBookingForm = (props: ComboBookingFormProps) => {
  const l = useLocalize();
  const [adultCount, setAdultCount] = React.useState('');
  const [kidCount, setKidCount] = React.useState('');
  const adultNum = parseNumber(adultCount);
  const kidNum = parseNumber(kidCount);

  const [services, setServices] = React.useState<ComboService[]>([]);
  const [serviceTime, _setServiceTime] = React.useState(
    () => new Map<ComboService, string>()
  );
  const setServiceTime = React.useCallback(function setServiceTime(
    service: ComboService,
    time: string
  ) {
    _setServiceTime(prevMap => new Map(prevMap.set(service, time)));
  },
  []);
  const [date, setDate] = React.useState('');
  const selectedComboPrice = React.useMemo(() => {
    return props.combo.prices.find(p => p.numOfService === services.length);
  }, [props.combo, services]);

  const [visitorType] = React.useContext(VisitorContext);
  const [adultPrice, kidPrice] = selectedComboPrice
    ? [selectedComboPrice.price, selectedComboPrice.price]
    : [0, 0];
  const [totalPrice, decimals] = getNumberParts(
    adultNum * adultPrice + kidNum * kidPrice
  );

  const { currentDateRef } = useMonthRange(null);
  const whatsappMsg = `${l('Combo')}: ${services
    .map(svc => `${svc.data.name.text} (${serviceTime.get(svc) || ''})`)
    .join(', ')}
${l('Date')}: ${date}
${l(visitorType === 'foreigner' ? 'Foreigner' : 'Malaysian')}
${l('Adult')}: ${adultCount || 0}, ${l('Kid')}: ${kidCount || 0}
${l('Total')}: ${totalPrice}.${decimals}
`;

  return (
    <>
      <fieldset>
        <DateField
          value={date}
          onChangeValue={setDate}
          minDate={currentDateRef.current}
          label={l('Date')}
          className="w-60"
          wrapperClassName="mb-4"
        />
        <CheckboxGroup
          values={services}
          name="service"
          onChangeValues={setServices}
        >
          {props.combo.include.map((inc, index) => {
            const details = inc.service.document[0];
            const selected = services.indexOf(details) > -1;

            return (
              <div
                className="flex items-center justify-between sm:justify-start space-x-2 mb-3"
                key={index}
              >
                <label className="flex items-start py-3 sm:w-60">
                  <div className="flex h-5 items-center">
                    <Checkbox
                      value={details}
                      className="h-4 w-4"
                      onChange={ev => {
                        if (
                          ev.target.checked &&
                          isFilledArray(details.data.timeOptions) &&
                          !isDefined(serviceTime.get(details))
                        ) {
                          setServiceTime(
                            details,
                            details.data.timeOptions[0].displayTime
                          );
                        }
                      }}
                    />
                  </div>
                  <div className="ml-3 leading-5">{details.data.name.text}</div>
                </label>
                {selected && isFilledArray(details.data.timeOptions) && (
                  <SelectField
                    value={serviceTime.get(details) || ''}
                    onChangeValue={time => setServiceTime(details, time)}
                    options={details.data.timeOptions.map(
                      opt => opt.displayTime
                    )}
                    required
                  />
                )}
              </div>
            );
          })}
        </CheckboxGroup>
      </fieldset>
      <fieldset className="my-3">
        <legend className="leading-loose my-1">{l('Basic Info')}</legend>
        <div className="space-y-2 sm:flex sm:space-x-2 sm:space-y-0">
          <VisitorTypeSelect wrapperClassName="flex-1 sm:w-48" />
          <TextField
            type="number"
            step={1}
            min={0}
            value={adultCount}
            onChangeValue={setAdultCount}
            placeholder={l('pax')}
            append={l('Adult')}
            className="w-20 xs:w-auto"
            wrapperClassName="flex-1 sm:w-0"
          />
          <TextField
            type="number"
            step={1}
            min={0}
            value={kidCount}
            onChangeValue={setKidCount}
            placeholder={l('pax')}
            append={l('Kid')}
            className="w-20 xs:w-auto"
            wrapperClassName="flex-1 sm:w-0"
          />
        </div>
      </fieldset>
      <div className="xs:flex justify-between items-center pt-3 border-t border-gray-300 mt-4">
        <output className="block mb-2">
          <small className="text-2xl mr-1">{l('RM')}</small>
          <span className="text-4xl">{totalPrice}</span>
          <small className="text-2xl">.{decimals}</small>
        </output>
        <div className="text-right">
          <MessageBtn
            message={whatsappMsg}
            onClick={() => {
              trackEvent({
                category: 'button',
                action: 'click',
                label: `order combo ${selectedComboPrice?.numOfService}`,
                value: totalPrice,
              });
            }}
            disabled={!date || totalPrice <= 0}
          />
        </div>
      </div>
    </>
  );
};

export const query = graphql`
  fragment ComboBookingForm on PrismicServiceCombo {
    data {
      include {
        service {
          document {
            data {
              name {
                text
              }
              timeOptions: time_options {
                displayTime
                time
              }
            }
          }
        }
      }
      prices {
        numOfService: num_of_service
        price
      }
    }
  }
`;

interface ComboService {
  data: {
    name: {
      text: string;
    };
    localAdultPrice: number;
    localKidPrice: number;
    foreignAdultPrice: number;
    foreignKidPrice: number;
    timeOptions: Array<{
      displayTime: string;
      time: string;
    }>;
  };
}

export interface ComboDetails {
  include: Array<{
    service: {
      document: Array<ComboService>;
    };
  }>;
  prices: Array<{
    numOfService: number;
    price: number;
  }>;
}
